import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ApprovalDetailField } from '../../components/ApprovalDetailField';
import { HrAdminApprovalFields } from '../../components/HrAdminApprovalFields';
import { ErrorState } from '../../components/ErrorState';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getTimePermissionModuleCapabilities } from '../../config/timePermissionCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  cancelTimePermissionRequest,
  getTimePermissionRequestDetails,
} from '../../services/timePermissionService';
import type { TimePermissionRequestDetails } from '../../types/timePermission';
import type { WorkflowDetails } from '../../types/workflow';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { timePermissionErrorMessage } from '../../utils/timePermissionErrors';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'TimePermissionDetails'
>;

function toApprovalDetails(
  permission: TimePermissionRequestDetails,
): WorkflowDetails {
  return {
    workflowInstanceId: permission.workflow?.workflowInstanceId ?? 0,
    requestTypeCode: 'PERMISSION_REQUEST',
    requestTypeLabel: 'Permission request',
    requestReferenceId: permission.permissionRequestId,
    requestNumber: permission.requestNumber,
    employee: {
      employeeId: permission.employee.employeeId,
      employeeNumber: permission.employee.employeeNumber,
      displayName: permission.employee.displayName,
      departmentName: null,
      designationName: permission.employee.designationName,
    },
    workflowStatusCode: permission.statusCode,
    workflowStatusLabel: permission.statusLabel,
    submittedAt: permission.submittedAt,
    completedAt: null,
    rejectReason: permission.rejectReason,
    cancellationReason: permission.cancellationReason,
    currentStep: permission.workflow
      ? {
          stepId: null,
          stepName: permission.workflow.currentStepName,
          stepNumber: null,
        }
      : null,
    steps: [],
    requestDetails: {
      permissionDate: permission.permissionDate,
      fromTime: permission.fromTime,
      toTime: permission.toTime,
      durationMinutes: permission.durationMinutes,
      permissionTypeName: permission.permissionType.permissionTypeName,
      permissionType: permission.permissionType.permissionTypeCode,
      reason: permission.reason,
      cancellationReason: permission.cancellationReason,
    },
    canApprove: false,
    canReject: false,
  };
}

export function TimePermissionRequestDetailsScreen({
  navigation,
  route,
}: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getTimePermissionModuleCapabilities(auth.permissions);
  const canAccess = auth.hasPermission(PERMISSIONS.TIME_PERMISSION_VIEW);
  const permissionRequestId = route.params.permissionRequestId;

  const [details, setDetails] =
    useState<TimePermissionRequestDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canAccess) {
      setLoading(false);
      return;
    }
    if (!Number.isInteger(permissionRequestId) || permissionRequestId <= 0) {
      setDetails(null);
      setError('Permission request was not found.');
      setLoading(false);
      return;
    }
    if (!capabilities.canViewDetails) {
      setDetails(null);
      setError('Permission request details are not currently available.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await getTimePermissionRequestDetails(permissionRequestId);
      setDetails(result);
    } catch (caught) {
      setDetails(null);
      setError(
        timePermissionErrorMessage(
          caught,
          'Unable to load permission request details.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [canAccess, capabilities.canViewDetails, permissionRequestId]);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const themed = useMemo(
    () => ({
      name: { ...typography.sectionTitle, color: palette.textPrimary },
      meta: { ...typography.caption, color: palette.textSecondary },
      actionError: {
        ...typography.caption,
        color: palette.error,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  const confirmCancel = async () => {
    const trimmedReason = cancelReason.trim();
    if (!trimmedReason) {
      setActionError('Cancellation reason is required.');
      return;
    }
    setCancelling(true);
    setActionError(null);
    try {
      await cancelTimePermissionRequest(permissionRequestId, trimmedReason);
      navigation.goBack();
    } catch (caught) {
      setActionError(
        timePermissionErrorMessage(
          caught,
          'Unable to cancel permission request.',
        ),
      );
    } finally {
      setCancelling(false);
    }
  };

  // Always show the request owner — never the active role / logged-in viewer.
  const employeeName =
    details?.employee.displayName?.trim() ||
    details?.employee.employeeNumber?.trim() ||
    'Permission Details';
  const permissionType = formatDisplayLabel(
    details?.permissionType.permissionTypeName ||
      details?.permissionType.permissionTypeCode,
    'Not available',
  );
  const approvalDetails = details ? toApprovalDetails(details) : null;

  return (
    <PermissionGuard
      permission={PERMISSIONS.TIME_PERMISSION_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Permission Details"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading permission details" />
        </AppScreen>
      ) : error && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Permission Details"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : details && approvalDetails ? (
        <AppScreen scroll>
          <ScreenHeader title={employeeName} onBack={navigation.goBack} />

          <View style={styles.body}>
            <AppCard style={styles.card}>
              <View style={styles.profileRow}>
                <ProfileAvatar name={employeeName} size={56} />
                <View style={styles.profileText}>
                  <Text style={themed.name}>{employeeName}</Text>
                  {details.employee.employeeNumber ? (
                    <Text style={themed.meta}>
                      {details.employee.employeeNumber}
                    </Text>
                  ) : null}
                  {details.employee.designationName ? (
                    <Text style={themed.meta}>
                      {details.employee.designationName}
                    </Text>
                  ) : null}
                </View>
                <LeaveStatusBadge
                  label={details.statusLabel}
                  statusCode={details.statusCode}
                />
              </View>

              <ApprovalDetailField
                label="Permission Type"
                value={permissionType}
                palette={palette}
              />
              <HrAdminApprovalFields
                details={approvalDetails}
                palette={palette}
              />
            </AppCard>

            {capabilities.canCancel && details.canCancel ? (
              showCancelReason ? (
                <View style={styles.cancelBlock}>
                  <AppInput
                    label="Cancellation reason"
                    value={cancelReason}
                    onChangeText={value => {
                      setCancelReason(value);
                      setActionError(null);
                    }}
                    placeholder="Enter reason for cancellation"
                  />
                  {actionError ? (
                    <Text accessibilityRole="alert" style={themed.actionError}>
                      {actionError}
                    </Text>
                  ) : null}
                  <AppButton
                    title="Confirm Cancel"
                    variant="danger"
                    onPress={confirmCancel}
                    loading={cancelling}
                    disabled={cancelling}
                  />
                  <AppButton
                    title="Keep Permission"
                    variant="text"
                    onPress={() => {
                      setShowCancelReason(false);
                      setCancelReason('');
                      setActionError(null);
                    }}
                    disabled={cancelling}
                  />
                </View>
              ) : (
                <AppButton
                  title="Cancel Permission"
                  variant="danger"
                  onPress={() => setShowCancelReason(true)}
                />
              )
            ) : null}
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: 0, marginBottom: spacing.md, paddingBottom: spacing.sm },
  profileRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  profileText: { flex: 1, gap: 2 },
  cancelBlock: { gap: spacing.md, marginBottom: spacing.xl },
});
